import React from "react"
import {LMTableApi, PaginationModel, SortModel} from "../components/Tables/types";
import PageTitle from "../components/subcomponents/PageTitle";
import {TablePaper} from "../components/TablePaper";
import {Alert, Box, Button, Divider} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import TableSearch from "../components/subcomponents/TableSearch";
import {InnerPaperContainer} from "../components/subcomponents/Containers/Containers";
import CustomerService from "../services/CustomerService";
import {ViewerCustomerTableColumns} from "../constants";
import TableFactory from "../components/TableFactory";
import ErrorExclamation from "../components/subcomponents/ErrorExclamation";
import {styled} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {DataGridCsvExporter} from "../export/csv/DataGridCSVExporter";
import {applySearchFilter, sortBySortModel, timeStamp} from "../utils/helperFunctions";
import {CUSTOMER_NUMBER_SORT_MODEL} from "../utils/customerNumberSort";
import {hasUnknownSalesperson, withSalespersonLabels} from "../utils/customerSalesperson";

const TableBox = styled(Box)({
  padding: "1.5rem 2rem"
})

export default function ViewerCustomerPage() {
  const apiRef = React.useRef<LMTableApi | null>(null);

  const [items, setItems] = React.useState<any[]>([])
  const [error, setError] = React.useState(false)
  const [isLoadingData, setLoadingData] = React.useState(false)
  const [searchFilter, setSearchFilter] = React.useState('')

  const [pagination, setPagination] = React.useState<PaginationModel>({page: 0, pageSize: 15});
  const [sortModel, setSortModel] = React.useState<SortModel>(CUSTOMER_NUMBER_SORT_MODEL);

  const {t} = useTranslation()

  React.useEffect(() => {
    loadData().then()
  }, [])

  async function loadData() {
    setLoadingData(true)
    try {
      setItems(await CustomerService.getCustomerTableData())
      setError(false)
    } catch (e) {
      console.error(e)
      setError(true)
    }
    setLoadingData(false)
  }

  const displayItems = React.useMemo(() => withSalespersonLabels(items, t), [items, t])

  const filteredItems = React.useMemo(
    () => applySearchFilter(displayItems, searchFilter),
    [displayItems, searchFilter]
  )

  // Sorted before slicing: this page hands LMTable a single page, which the table
  // can only order within — see CustomerPage.
  const sortedItems = React.useMemo(
    () => sortBySortModel(filteredItems, sortModel, ViewerCustomerTableColumns),
    [filteredItems, sortModel]
  )

  const pagedItems = React.useMemo(() => {
    const start = pagination.page * pagination.pageSize
    return sortedItems.slice(start, start + pagination.pageSize)
  }, [sortedItems, pagination])

  function exportCSV() {
    if (!apiRef.current) return;
    // The viewer table's own columns, so the export holds only what a viewer sees.
    const columns = apiRef.current.getVisibleColumns()

    // sortedItems, not apiRef.getSortedRows(): the table only holds the current page
    // — see CustomerPage.
    const exporter = new DataGridCsvExporter(sortedItems, columns, {
      filename: `Kunden Tabellen Export ${timeStamp()}`,
      delimiter: ";",
      includeHeaders: true,
    });

    exporter.exportToCsv();
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t('appBar.customer')}</PageTitle>
      <Divider/>
      <TablePaper>
        <InnerPaperContainer>
          <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 2,
                marginTop: 0,
                paddingTop: 0,
                paddingLeft: 2,
                paddingRight: 2,
                flexFlow: 'row wrap'
              }}>
            <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)'}}>
              <div style={{display: 'flex', justifyContent: 'left'}}>
                <TableSearch onSearch={(input) => {
                  setSearchFilter(input)
                }}/>
              </div>
            </Box>

            <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)'}}>
              <div style={{display: 'flex', justifyContent: 'right'}}>
                <Button
                  variant="contained"
                  disabled={error || isLoadingData}
                  onClick={exportCSV}
                  startIcon={<DownloadIcon/>}
                >{t('pages.exportCSV')}</Button>
              </div>
            </Box>
          </Box>
        </InnerPaperContainer>
        <TableBox>
          {!error && hasUnknownSalesperson(items) &&
            <Alert severity="warning" sx={{mb: 2}}>{t('pages.customerPage.salespersonLoadIncomplete')}</Alert>}
          {error ?
            <ErrorExclamation message={t('activities.dataFetchFail')}/>
            : TableFactory.createTable(
              "customers",
              ViewerCustomerTableColumns.map(elem => elem.field),
              pagedItems,
              [],
              () => {},
              (model) => {
                setSortModel(model)
              },
              {sortModel},
              apiRef,
              pagination,
              setPagination,
              filteredItems.length,
              isLoadingData
            )}
        </TableBox>
      </TablePaper>
    </div>
  )
}
